export type AIWorkshopConfig = {
  id: string;
  title: string;
  startsAt: string;
  durationMinutes: number;
  timeZone: string;
  kitFormUid: string | null;
  kitFormUrl: string | null;
  registrationReady: boolean;
  meetingUrl: string | null;
};

// Replace this event record when the next monthly workshop is confirmed.
// Activate registration only after verifying the dedicated form fields,
// double-opt-in confirmation redirect, and workshop meeting access details.
export const AI_WORKSHOP: AIWorkshopConfig = {
  id: "2026-10-15-first-useful-workflow",
  title: "Move Beyond Prompts With AI Agent Workflows",
  startsAt: "2026-10-15T14:00:00Z",
  durationMinutes: 60,
  timeZone: "America/Chicago",
  kitFormUid: "530246866e",
  kitFormUrl: "https://techleaders.kit.com/530246866e",
  // This event has ended. Do not reactivate its saved Kit registration form.
  registrationReady: false,
  meetingUrl: "https://meet.google.com/ezs-bvwz-aqn",
};

export const AI_WORKSHOP_PATH = "/ai-workshop";
export const AI_WORKSHOP_URL = `https://www.technical-leaders.com${AI_WORKSHOP_PATH}`;
export const WORKSHOP_CONTACT = "mailto:todd@technical-leaders.com";

export const getWorkshopEnd = (workshop = AI_WORKSHOP): Date =>
  new Date(new Date(workshop.startsAt).getTime() + workshop.durationMinutes * 60_000);

// Registration closes at the event start, without inventing a future date.
export const isWorkshopRegistrationOpen = (now: Date, workshop = AI_WORKSHOP): boolean =>
  now.getTime() < new Date(workshop.startsAt).getTime();

export const formatWorkshopDate = (workshop = AI_WORKSHOP): string =>
  new Intl.DateTimeFormat("en-US", { timeZone: workshop.timeZone, weekday: "long", month: "long", day: "numeric", year: "numeric" }).format(new Date(workshop.startsAt));

export const formatWorkshopTime = (workshop = AI_WORKSHOP): string =>
  `${new Intl.DateTimeFormat("en-US", { timeZone: workshop.timeZone, hour: "numeric", minute: "2-digit" }).format(new Date(workshop.startsAt)).toLowerCase().replace("am", "a.m.").replace("pm", "p.m.")} Central Time`;

export const buildWorkshopAI30Path = (search = ""): string => {
  const incoming = new URLSearchParams(search);
  const params = new URLSearchParams({ source: "ai-workshop", audience: "organization" });
  for (const key of ["workflow", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
    const value = incoming.get(key);
    if (value) params.set(key, value);
  }
  return `/ai-30?${params}`;
};

const calendarStamp = (date: Date) => date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const calendarDescription = (workshop: AIWorkshopConfig) => "Free practical workshop. Bring one recurring task and learn to define a workflow, review a draft, and check its quality. Workshop page: " + AI_WORKSHOP_URL + ". This is a personal reminder, not registration." + (workshop.meetingUrl ? " Meeting access: " + workshop.meetingUrl : " Meeting access details are not yet available.");
const escapeCalendarText = (value: string) => value.replace(/\\/g, "\\\\").replace(/\r?\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");

// iCalendar content lines are limited to 75 UTF-8 bytes before folding.
const foldCalendarLine = (line: string): string => {
  let folded = "";
  let bytes = 0;
  for (const character of line) {
    const size = new TextEncoder().encode(character).length;
    if (bytes + size > 75) { folded += "\r\n "; bytes = 1; }
    folded += character;
    bytes += size;
  }
  return folded;
};

export const buildWorkshopIcs = (workshop = AI_WORKSHOP, createdAt = new Date()): string => [
  "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Technical Leaders//AI Workshop//EN", "CALSCALE:GREGORIAN", "BEGIN:VEVENT",
  `UID:${workshop.id}@technical-leaders.com`, `DTSTAMP:${calendarStamp(createdAt)}`,
  `DTSTART:${calendarStamp(new Date(workshop.startsAt))}`, `DTEND:${calendarStamp(getWorkshopEnd(workshop))}`,
  `SUMMARY:${escapeCalendarText(workshop.title)}`, `DESCRIPTION:${escapeCalendarText(calendarDescription(workshop))}`,
  `LOCATION:${escapeCalendarText(workshop.meetingUrl || "Online")}`, `URL:${AI_WORKSHOP_URL}`, "END:VEVENT", "END:VCALENDAR",
].map(foldCalendarLine).join("\r\n") + "\r\n";

export const buildWorkshopGoogleCalendarUrl = (workshop = AI_WORKSHOP): string => {
  const params = new URLSearchParams({ action: "TEMPLATE", text: workshop.title,
    dates: `${calendarStamp(new Date(workshop.startsAt))}/${calendarStamp(getWorkshopEnd(workshop))}`,
    details: calendarDescription(workshop), location: workshop.meetingUrl || "Online", ctz: workshop.timeZone });
  return `https://calendar.google.com/calendar/render?${params}`;
};
